import { useTranslation } from "react-i18next";
import Button from "../../../../components/common/Button/Button";
import images from "../../../../images";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useLazyGetQuestListQuery } from "../../../../services/apiSlice";
import CardQuestPerType from "../../../../components/common/Card/CardQuestPerType";
import { useNavigate } from "react-router-dom";
import CommonModal from "../../../../components/common/Modal/ModalCommon";
import BodyModalLoading from "../../../../components/common/Modal/BodyModalLoading/BodyModalLoading";
import LoadingCommon from "../../../../components/common/LoadingStatus/LoadingCommon";

const StepQuestUnlocked = ({ questDetailsData, onNavigateRequest }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [quests, setQuests] = useState([]);
  const [isParticipantsCompleted, setIsParticipantsCompleted] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [dragMoved, setDragMoved] = useState(false);

  const scrollContainerRef = useRef(null);
  const loadingQuestModalRef = useRef(null);

  const [getListQuests, { isLoading: isLoadingQuests }] =
    useLazyGetQuestListQuery();

  const accessToken =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  const introductionQuestTypes = ["INTRODUCE", "DEEP_DIVE"];
  const isIntroCompleted = quests.some(
    (quest) =>
      introductionQuestTypes.includes(quest.quest_business.quest_type) &&
      quest.consumer_status === "COMPLETED",
  );

  const performNavigation = (path, state) => {
    if (onNavigateRequest) {
      onNavigateRequest(path, state);
    } else {
      navigate(path, state);
    }
  };

  const checkParticipantsStatus = useCallback((data) => {
    if (!data?.data || !Array.isArray(data.data) || data.data.length === 0) {
      setIsParticipantsCompleted(false);
      return;
    }

    const allQuestsCompleted = data.data
      .filter((quest) => quest.quest_business.quota_type === "DAILY")
      .every(
        (quest) =>
          Array.isArray(quest.participants) &&
          quest.participants.length > 0 &&
          quest.participants.length === 2 &&
          quest.participants.every(
            (participant) => participant.status === "COMPLETED",
          ),
      );

    setIsParticipantsCompleted(allQuestsCompleted);
  }, []);

  const fetchQuests = useCallback(
    async (newLimit, append = false) => {
      const params = {
        limit: newLimit,
        id_location: questDetailsData?.location?.id,
      };

      try {
        const response = await await getListQuests(params).unwrap();

        const normalizedQuests = (response?.data || []).map((quest) => ({
          ...quest,
          participants: Array.isArray(quest.participants)
            ? quest.participants
            : [],
        }));

        setQuests((prev) =>
          append ? [...prev, ...normalizedQuests] : normalizedQuests,
        );

        checkParticipantsStatus({ ...response, data: normalizedQuests });
      } catch (error) {
        console.error("Error fetching quests:", error);
      }
    },
    [getListQuests, checkParticipantsStatus, questDetailsData],
  );

  useEffect(() => {
    fetchQuests(20, false);
  }, [fetchQuests]);

  const nextQuest = useMemo(() => {
    if (!quests || quests.length === 0) return null;

    return quests.find((q) => {
      const isNotStarted = ["COMPLETED", "REVIEWING"].includes(
        q.consumer_status,
      );
      const isEnabled = q.is_enable && Boolean(q.slot_available);
      const isBizActive = ["PUBLISHED", "IN_PROGRESS"].includes(
        q.quest_business?.status_biz,
      );

      return !isNotStarted && isEnabled && isBizActive;
    });
  }, [quests]);

  const handleNextQuest = async () => {
    if (questDetailsData?.is_rally_quest) {
      performNavigation("/quest");
      return;
    }

    if (!nextQuest) {
      performNavigation(`/quest/location/${questDetailsData?.location?.id}`);
    } else {
      const queryParams = new URLSearchParams({
        quest_id: nextQuest.id,
        auto_open: "true",
      }).toString();

      performNavigation(
        `/quest/location/${questDetailsData?.location?.id}?${queryParams}`,
      );
    }
  };
  const handleMouseDown = (e) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setDragMoved(false);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    scrollContainerRef.current.style.cursor = "grabbing";
  };

  const handleMouseLeaveOrUp = () => {
    if (!scrollContainerRef.current) return;
    setIsDragging(false);
    scrollContainerRef.current.style.cursor = "grab";
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    setDragMoved(true);
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleClickCapture = (e) => {
    if (dragMoved) {
      e.stopPropagation();
    }
  };

  return (
    <div className="bg-white flex flex-col gap-6 justify-center items-center w-full  py-6 px-[24px] xs:py-9 xs:px-[30px] ">
      <LoadingCommon statusLoading={isLoadingQuests} />

      <div className="text-[#253FF6] text-center font-[Figtree] text-base xs:text-xl font-semibold ">
        <span>{t("quest.completedQuest.newQuestUnlocked")}</span>
      </div>
      <div
        className="w-[70px] h-[70px] rounded-md bg-cover bg-center bg-[#E9E6E4]"
        style={{
          backgroundImage: questDetailsData?.quest_business?.business
            ?.avatar_data?.file_path
            ? `url(${process.env.REACT_APP_URL_IMAGE}/${questDetailsData?.quest_business?.business?.avatar_data?.file_path})`
            : `url(${images.DefaultImageQuest})`,
        }}
      ></div>

      <div className="flex w-full flex-col gap-2 xs:gap-3 justify-center items-center">
        <div
          className="flex flex-row gap-6 overflow-x-scroll scrollbar-hide quest-line-track w-full"
          ref={scrollContainerRef}
          style={{ cursor: "grab" }}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeaveOrUp}
          onMouseUp={handleMouseLeaveOrUp}
          onMouseMove={handleMouseMove}
          onClickCapture={handleClickCapture}
        >
          {quests
            ?.slice()
            // .sort((a, b) => {
            //   const isACompletedOrFull =
            //     a.consumer_status === "COMPLETED" ||
            //     a.consumer_status === "FULL";
            //   const isBCompletedOrFull =
            //     b.consumer_status === "COMPLETED" ||
            //     b.consumer_status === "FULL";

            //   if (isACompletedOrFull && !isBCompletedOrFull) return 1;
            //   if (!isACompletedOrFull && isBCompletedOrFull) return -1;
            //   return 0;
            // })
            .map((quest) => (
              <CardQuestPerType
                key={quest.id}
                slotAvailable={quest.slot_available}
                consumerStatus={
                  !accessToken &&
                  (quest?.quest_business?.status_biz === "IN_PROGRESS" ||
                    quest?.quest_business?.status_biz === "PUBLISHED")
                    ? "NOT_STARTED"
                    : !accessToken &&
                        quest?.quest_business?.status_biz === "EXPIRED"
                      ? "EXPIRED"
                      : !accessToken &&
                          quest?.quest_business?.status_biz === "COMPLETED"
                        ? "FULL"
                        : Array.isArray(quest?.participants) &&
                            quest.participants.length > 0
                          ? quest.participants[quest.participants.length - 1]
                              ?.status
                          : quest.consumer_status
                }
                questType={quest.quest_business.quest_type}
                rallyCoin={quest.quest_business.token}
                isIntroCompleted={isIntroCompleted}
                isEnable={
                  !accessToken &&
                  (quest?.quest_business?.status_biz === "IN_PROGRESS" ||
                    quest?.quest_business?.status_biz === "PUBLISHED")
                    ? true
                    : !accessToken &&
                        (quest?.quest_business?.status_biz === "EXPIRED" ||
                          quest?.quest_business?.status_biz === "COMPLETED")
                      ? false
                      : quest.is_enable
                }
                expireTime={
                  Array.isArray(quest.participants) &&
                  quest.participants.length > 0
                    ? quest.participants.find(
                        (participant) => participant.status === "PAUSED_QUEST",
                      )?.expired_at
                    : null
                }
                participantId={
                  Array.isArray(quest.participants) &&
                  quest.participants.length > 0
                    ? quest.participants.find(
                        (participant) => participant.status === "PAUSED_QUEST",
                      )?.id
                    : null
                }
                questId={quest.id}
                isParticipantsCompleted={isParticipantsCompleted}
                quest={quest}
                isClickDisabled={true}
                showConnector={true}
                reward_stickers={quest.reward_stickers}
              />
            ))}
        </div>
      </div>
      <Button
        text={
          questDetailsData?.is_rally_quest
            ? t("quest.completedQuest.backtoHome")
            : t("quest.completedQuest.nextQuest")
        }
        className="flex-1 mt-0 gap-2.5 h-[50px] self-stretch rounded-md p-3 text-white bg-[#253FF6]  text-base xs:text-xl font-medium text-center"
        variant="primary"
        onClick={handleNextQuest}
      />
      <Button
        text={t("quest.completedQuest.withDrawCash")}
        className="flex-1 mt-0 gap-2.5 h-[50px] self-stretch rounded-md p-3 text-white bg-[#253FF6]  text-base xs:text-xl font-medium text-center"
        variant="primary"
        onClick={() => {
          performNavigation("/profile");
        }}
      />
      <CommonModal
        ref={loadingQuestModalRef}
        classNameText="text-[14px] font-medium text-[#253FF6]"
        showCloseIcon={false}
        canClickOverLay={false}
        classNameModal="p-6"
        classNameBodyModal="bg-[#ECEEFF] p-0"
      >
        <BodyModalLoading
          duration="5s"
          loadingText={t("quest.questDetails.loadingQuest")}
          imageSrc={images.ReviewingQuestCompletedImage}
          tipsText1={t("quest.questDetails.honestyPays")}
          iconText1="💰"
          tipsText2={t("quest.questDetails.vaguenessDoesnt")}
          iconText2="❌"
          conclusionText={t("quest.questDetails.beSpecificEarnMore")}
          animationSrc={"write-and-earn.json"}
        />
      </CommonModal>
    </div>
  );
};
export default StepQuestUnlocked;
